"use client";

import Image from "@/lib/compat/image";
import { useState } from "react";
import clsx from "clsx";
import { attestorRole } from "@/lib/attestors/roles";

/**
 * AttestorLogoSlot renders a placeholder mark when no logo file exists for
 * the partner OrbID, and renders the partner logo from
 * frontend/public/attestors/<orbid>.{svg,png} when present. SVG is tried
 * first (crisp scaling); PNG is the fallback so partners whose marks ship
 * as bitmaps don't need conversion before going live.
 *
 * Hover behaviour (D2 from plan v3): if the orbid is in the attestor-roles
 * registry, the tooltip surfaces the human-readable role + jurisdiction so
 * audiences who don't know what "FUC" / "ABCC" / "CDIAL" stand for can
 * read it on hover. Falls back to the placeholder copy when the role is
 * unregistered.
 */
const EXTENSIONS = ["svg", "png"] as const;

export function AttestorLogoSlot({
  partnerOrbId,
  fallbackName,
  size = 28,
  className,
}: {
  partnerOrbId: string;
  fallbackName: string;
  size?: number;
  className?: string;
}) {
  const fileName = partnerOrbId.replace(/[^a-z0-9-]/gi, "-").toLowerCase();
  const [extIdx, setExtIdx] = useState(0);
  const imgFailed = extIdx >= EXTENSIONS.length;
  const [hovered, setHovered] = useState(false);
  const role = attestorRole(partnerOrbId);

  // Tooltip body: prefer the registered role; otherwise fall back to
  // the "partner activation" placeholder copy.
  const tooltipNode = role ? (
    <div className="flex max-w-[260px] flex-col gap-0.5">
      <span className="font-display text-[10px] font-semibold text-orbitra-paper">{role.name}</span>
      <span className="text-[10px] text-orbitra-teal-soft">{role.role}</span>
    </div>
  ) : (
    <span className="text-orbitra-teal-soft">
      Attestation partner — logo to be replaced with {fallbackName} mark on partner activation.
    </span>
  );

  if (imgFailed) {
    return (
      <span
        className={clsx("relative inline-flex items-center justify-center", className)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span
          className="flex items-center justify-center rounded-md border border-state-traced/40 bg-orbitra-ink/60 text-state-traced font-display font-semibold cursor-help"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.32) }}
          aria-label={role ? `${role.name} · ${role.role}` : `${fallbackName} (attestor logo placeholder)`}
          title={role ? `${role.name} · ${role.role}` : undefined}
        >
          {fallbackName.split(/\s+/).map((w) => w[0]?.toUpperCase()).slice(0, 3).join("")}
        </span>
        {hovered && (
          <span className="absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 rounded bg-orbitra-ink/95 px-2 py-1.5 text-[10px] shadow-glass">
            {tooltipNode}
          </span>
        )}
      </span>
    );
  }

  // Wider container for wide bitmap logos (ABCC) — `object-contain` keeps
  // crests/square seals (CDIAL Halal) centred without distortion while
  // wordmarks fill the available width.
  return (
    <span
      className={clsx("relative inline-flex items-center justify-center overflow-hidden", className)}
      style={{ width: size * 1.8, height: size }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={role ? `${role.name} · ${role.role}` : undefined}
    >
      <Image
        key={EXTENSIONS[extIdx]}
        src={`/attestors/${fileName}.${EXTENSIONS[extIdx]}`}
        alt={role?.name ?? fallbackName}
        width={size * 1.8}
        height={size}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        onError={() => setExtIdx((i) => i + 1)}
      />
      {hovered && role && (
        <span className="absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 rounded bg-orbitra-ink/95 px-2 py-1.5 text-[10px] shadow-glass">
          {tooltipNode}
        </span>
      )}
    </span>
  );
}
